import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Radar as RadarIcon, Lock, Sparkles, MessageSquare, Search, Loader2, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { PLANS_BY_LANG } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import {
  useCheckoutConsents,
  CheckoutConsentCheckboxes,
  checkoutPayload,
} from "@/components/CheckoutConsent";
import {
  RADAR_CONFIRMED,
  RADAR_OBSERVATION,
  RADAR_ARCHIVE,
  RADAR_MISSING_TEXT,
  RADAR_MISSING_DATE_TEXT,
  RADAR_LAST_UPDATE,
  RADAR_NEXT_UPDATE,
  RADAR_FREE_SAMPLE_ID,
  radarUnlocked,
} from "@/constants/radarData";

/**
 * LEO RADAR — production information page (MVP, static data).
 * Full access: leo_start, leo_monthly, leo_annual.
 * Paywall: free, single, sprint → one full sample record + existing /pricing flow.
 */

// Field order + labels for the "POTWIERDZONY ETAP" section.
const CONFIRMED_FIELDS = [
  ["type", "Typ"],
  ["country", "Kraj"],
  ["language", "Język"],
  ["platform", "Platforma"],
  ["status", "Status"],
  ["director", "Reżyseria"],
  ["script", "Scenariusz"],
  ["producer", "Produkcja"],
  ["producerPerson", "Producent"],
  ["plProducer", "Polski producent"],
  ["coProducers", "Koprodukcja"],
  ["financing", "Finansowanie"],
  ["knownCast", "Znana obsada"],
  ["shootLocation", "Lokacja potwierdzona"],
];
// These four are always shown (with "Brak..." fallback where missing).
const CONFIRMED_ALWAYS_SHOWN = [
  ["knownCastAlways", "Obsada"],
  ["castingDirectorAlways", "Casting director"],
  ["shootPeriodAlways", "Zdjęcia"],
];

// Field order + labels for the "DO OBSERWACJI" section.
const OBSERVATION_FIELDS = [
  ["type", "Typ"],
  ["status", "Status"],
  ["director", "Reżyseria"],
  ["script", "Scenariusz"],
  ["producer", "Produkcja"],
];

function DlRow({ label, value, missing }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2">
      <dt className="mono text-[10px] tracking-[0.2em] uppercase text-amber-500/80 shrink-0 sm:min-w-[9rem]">
        {label}
      </dt>
      <dd className={missing ? "text-stone-500 italic" : "text-stone-200"}>
        {value}
      </dd>
    </div>
  );
}

// Very small markdown → HTML for the contact/pretexts response: bold **x**,
// links [t](u), and simple lists / headings. No external dep, no XSS risk
// (we only accept output we asked the model to produce, and escape everything
// else). Enough for LEO Radar's `## KONTAKT` / `## PRETEKSTY` payload.
function renderRadarMarkdown(md) {
  if (!md) return "";
  const esc = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    let l = esc(line);
    l = l.replace(/\*\*(.+?)\*\*/g, '<strong class="text-stone-100">$1</strong>');
    l = l.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-amber-400 underline hover:text-amber-300">$1</a>'
    );
    if (/^##\s+/.test(line)) {
      out.push(
        `<div class="mono text-[10px] tracking-[0.25em] uppercase text-amber-400 mt-3 mb-1">${l
          .replace(/^##\s+/, "")}</div>`
      );
    } else if (/^\s*[-*]\s+/.test(line)) {
      out.push(`<div class="pl-3 text-stone-200 text-sm leading-relaxed">• ${l
        .replace(/^\s*[-*]\s+/, "")}</div>`);
    } else if (/^\s*\d+\.\s+/.test(line)) {
      out.push(`<div class="pl-3 text-stone-200 text-sm leading-relaxed">${l}</div>`);
    } else if (line.trim() === "") {
      out.push('<div class="h-2"></div>');
    } else {
      out.push(`<div class="text-stone-200 text-sm leading-relaxed">${l}</div>`);
    }
  }
  return out.join("");
}

function ConfirmedCard({ p }) {
  const [showFinder, setShowFinder] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null); // "reżyser" | "producent" | "casting director"
  const [selectedName, setSelectedName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { markdown }
  const [error, setError] = useState(null);
  const [crmPrefill, setCrmPrefill] = useState(null);

  const peopleOptions = [
    p.director ? { role: "reżyser", name: p.director, label: "Reżyser" } : null,
    p.producer ? { role: "producent", name: p.producer, label: "Producent" } : null,
    p.castingDirector
      ? { role: "casting director", name: p.castingDirector, label: "Casting director" }
      : null,
  ].filter(Boolean);

  const runSearch = async (role, name) => {
    setSelectedRole(role);
    setSelectedName(name);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // Async job: POST returns a job_id immediately, then we poll until
      // the Gemini + Google Search worker finishes (or errors / expires).
      const start = await api.post("/radar/find-contact", {
        person_name: name,
        person_role: role,
        production_title: p.title || "",
      });
      const jobId = start?.data?.job_id;
      if (!jobId) throw new Error("Brak identyfikatora zadania.");
      const deadline = Date.now() + 70_000; // worker caps at 60s; keep slack
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) {
          throw new Error("Wyszukiwanie trwa zbyt długo. Spróbuj ponownie.");
        }
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/radar/find-contact/${jobId}`);
        const st = poll?.data?.status;
        if (st === "done") {
          setResult({ markdown: poll.data.markdown || "" });
          return;
        }
        if (st === "error") {
          throw new Error(poll.data.error || "Wyszukiwanie nie powiodło się.");
        }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Wyszukiwanie nie powiodło się.");
    } finally {
      setLoading(false);
    }
  };

  const openLeoMessage = () => {
    const detail = {
      person_name: selectedName || null,
      person_role: selectedRole || null,
      production_title: p.title,
      director: p.director || null,
      producer: p.producer || null,
      castingDirector: p.castingDirector || null,
      contact_markdown: result?.markdown || null,
    };
    window.dispatchEvent(new CustomEvent("open-leo-with-context", { detail }));
  };

  const openAddToCrm = () => {
    // First-time hint: show a short helper toast on the very first click,
    // once per browser, so users understand what CRM is for. No checkbox,
    // no modal, no blocking — the dialog opens as usual right after.
    try {
      if (!localStorage.getItem("leo_radar_crm_hint_shown")) {
        toast.info(
          "CRM służy do zarządzania Twoimi kontaktami zawodowymi. Zapisuj wyłącznie informacje potrzebne do prowadzenia relacji zawodowych. Nie zapisuj zbędnych informacji prywatnych ani danych wrażliwych.",
          { duration: 8000 }
        );
        localStorage.setItem("leo_radar_crm_hint_shown", "1");
      }
    } catch { /* ignore storage errors */ }
    // Fall back to whichever person is currently visible on this card, in
    // the same priority order the finder shows them. Never guess data.
    const role =
      selectedRole ||
      (p.director ? "reżyser" : p.producer ? "producent" : p.castingDirector ? "casting director" : null);
    if (!role) return;
    const name =
      selectedName ||
      (role === "reżyser"
        ? p.director
        : role === "producent"
        ? p.producer
        : role === "casting director"
        ? p.castingDirector
        : "") || "";
    const typeMap = {
      "reżyser": "director",
      "producent": "producer",
      "casting director": "casting_director",
    };
    const parts = name.trim().split(/\s+/);
    setCrmPrefill({
      first_name: parts[0] || "",
      last_name: parts.slice(1).join(" "),
      type: typeMap[role] || "casting_director",
      company: role === "producent" ? (p.producer || "") : "",
      email: "",
      project_context: p.title || "",
    });
  };

  return (
    <article
      data-testid={`radar-production-${p.id}`}
      className="paper p-5 sm:p-6"
    >
      <h3 className="serif text-2xl sm:text-3xl text-stone-100">{p.title}</h3>
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        {CONFIRMED_FIELDS.map(([key, label]) => {
          const v = p[key];
          if (v == null || v === "") return null;
          return <DlRow key={key} label={label} value={v} />;
        })}
        {/* Always shown fields with fallback */}
        <DlRow
          label="Obsada"
          value={p.knownCast || RADAR_MISSING_TEXT}
          missing={!p.knownCast}
        />
        <DlRow
          label="Casting director"
          value={p.castingDirector || RADAR_MISSING_TEXT}
          missing={!p.castingDirector}
        />
        <DlRow
          label="Zdjęcia"
          value={p.shootPeriod || RADAR_MISSING_DATE_TEXT}
          missing={!p.shootPeriod}
        />
      </dl>
      {p.whyOnRadar && (
        <div className="mt-5 paper p-4 border-amber-500/25 bg-amber-500/5">
          <div className="mono text-[10px] tracking-[0.25em] uppercase text-amber-400 mb-2">
            Dlaczego na radarze
          </div>
          <p className="text-stone-200 text-sm leading-relaxed">
            {p.whyOnRadar}
          </p>
        </div>
      )}
      {p.castingUrl && (
        <a
          href={p.castingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-3 text-amber-400 underline text-sm"
          data-testid={`radar-casting-link-${p.id}`}
        >
          Zgłoś się do castingu
        </a>
      )}

      {/* --- Chcesz nawiązać kontakt? --- */}
      {peopleOptions.length > 0 && (
        <div className="mt-5 paper p-4 border-stone-800 bg-stone-900/40">
          <div className="mono text-[10px] tracking-[0.25em] uppercase text-stone-400 mb-3">
            Chcesz nawiązać kontakt?
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid={`radar-find-contact-${p.id}`}
              onClick={() => setShowFinder((v) => !v)}
              className="pill-btn !py-2 !px-3 text-xs inline-flex items-center gap-2"
            >
              <Search size={13} />
              Znajdź kontakt i pretekst
            </button>
            <button
              type="button"
              data-testid={`radar-write-with-leo-${p.id}`}
              onClick={openLeoMessage}
              className="pill-btn !py-2 !px-3 text-xs inline-flex items-center gap-2"
            >
              <MessageSquare size={13} />
              Napisz wiadomość z LEO
            </button>
            <button
              type="button"
              data-testid={`radar-add-to-crm-${p.id}`}
              onClick={openAddToCrm}
              className="pill-btn !py-2 !px-3 text-xs inline-flex items-center gap-2"
            >
              <UserPlus size={13} />
              Dodaj do CRM
            </button>
          </div>

          {showFinder && (
            <div className="mt-4 space-y-3">
              <div className="mono text-[10px] tracking-[0.2em] uppercase text-stone-500">
                Wybierz osobę
              </div>
              <div className="flex flex-wrap gap-2">
                {peopleOptions.map((o) => (
                  <button
                    key={o.role}
                    type="button"
                    data-testid={`radar-person-${p.id}-${o.role.replace(/\s+/g, "-")}`}
                    onClick={() => runSearch(o.role, o.name)}
                    disabled={loading}
                    className={`text-xs px-3 py-2 rounded-sm border transition-colors ${
                      selectedRole === o.role
                        ? "border-amber-500 bg-amber-500/10 text-amber-300"
                        : "border-stone-700 hover:border-amber-500/60 text-stone-200"
                    }`}
                  >
                    <span className="mono text-[9px] tracking-widest uppercase text-stone-500 block">
                      {o.label}
                    </span>
                    <span>{o.name}</span>
                  </button>
                ))}
              </div>

              {loading && (
                <div className="text-amber-400 mono text-xs tracking-widest inline-flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" />
                  Szukam w Google…
                </div>
              )}
              {error && (
                <div className="text-rose-400 text-xs">{error}</div>
              )}
              {result?.markdown && (
                <div
                  data-testid={`radar-contact-result-${p.id}`}
                  className="paper p-4 border-amber-500/20 bg-stone-950/60"
                  dangerouslySetInnerHTML={{
                    __html: renderRadarMarkdown(result.markdown),
                  }}
                />
              )}
              {result?.markdown && (
                <div
                  data-testid={`radar-about-contact-${p.id}`}
                  className="text-[11px] leading-relaxed text-stone-500 italic px-1 pt-1"
                >
                  <div className="mono text-[10px] tracking-[0.25em] uppercase text-stone-500 not-italic mb-1">
                    O kontakcie
                  </div>
                  LEO wyszukuje informacje zawodowe w publicznie dostępnych źródłach. Przed wykorzystaniem kontaktu zweryfikuj jego aktualność i zawodowy charakter. Znalezienie danych kontaktowych nie oznacza zgody danej osoby na otrzymywanie dowolnego rodzaju wiadomości. LEO nie wysyła wiadomości w Twoim imieniu — to Ty decydujesz, czy i w jaki sposób nawiążesz kontakt.
                </div>
              )}
              {result?.markdown && (
                <button
                  type="button"
                  data-testid={`radar-write-with-leo-after-${p.id}`}
                  onClick={openLeoMessage}
                  className="pill-btn !py-2 !px-3 text-xs inline-flex items-center gap-2"
                >
                  <Sparkles size={13} />
                  Napisz wiadomość z LEO
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <ContactFormDialog
        open={crmPrefill !== null}
        onOpenChange={(o) => {
          if (!o) setCrmPrefill(null);
        }}
        prefill={crmPrefill}
        onSaved={() => {
          setCrmPrefill(null);
          toast.success("Dodano do CRM");
        }}
      />
    </article>
  );
}

function ObservationCard({ p }) {
  return (
    <article
      data-testid={`radar-production-${p.id}`}
      className="paper p-5 sm:p-6"
    >
      <h3 className="serif text-2xl sm:text-3xl text-stone-100">{p.title}</h3>
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        {OBSERVATION_FIELDS.map(([key, label]) => {
          const v = p[key];
          if (v == null || v === "") return null;
          return <DlRow key={key} label={label} value={v} />;
        })}
        <DlRow
          label="Obsada"
          value={RADAR_MISSING_TEXT}
          missing
        />
        <DlRow
          label="Casting director"
          value={RADAR_MISSING_TEXT}
          missing
        />
        <DlRow label="Zdjęcia" value={RADAR_MISSING_DATE_TEXT} missing />
      </dl>
      {p.signal && (
        <div className="mt-5 paper p-4 border-amber-500/25 bg-amber-500/5">
          <div className="mono text-[10px] tracking-[0.25em] uppercase text-amber-400 mb-2">
            Sygnał
          </div>
          <p className="text-stone-200 text-sm leading-relaxed">{p.signal}</p>
        </div>
      )}
    </article>
  );
}

export default function Radar() {
  const { access, lang, user } = useAuth();
  const nav = useNavigate();
  const unlocked = radarUnlocked(access);
  const sampleForFree = RADAR_CONFIRMED.find((p) => p.id === RADAR_FREE_SAMPLE_ID) || RADAR_CONFIRMED[0];
  const [busy, setBusy] = useState(null);
  // Fire `radar_viewed` at most ONCE per mount (i.e. once per Radar
  // open / navigation). React 18 StrictMode double-mounts in dev, and
  // re-renders happen on any state change — the ref guarantees the
  // POST /analytics/track is not duplicated for the same visit.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    if (!user) return; // trackEvent no-ops without auth anyway; skip cleanly.
    viewedRef.current = true;
    trackEvent("radar_viewed", { unlocked });
  }, [user, unlocked]);
  const { consentOf, setConsent, canBuy } = useCheckoutConsents();
  const plans = PLANS_BY_LANG[lang] || PLANS_BY_LANG.pl;
  const unlockPlans = ["leo_start", "leo_monthly", "leo_light", "leo_annual"]
    .map((id) => plans.find((p) => p.id === id))
    .filter(Boolean);

  const buy = async (planId) => {
    if (!user) { nav("/login"); return; }
    if (!canBuy(planId)) return;
    const suffix = lang === "pl" ? "_pln" : "_usd";
    setBusy(planId);
    try {
      const r = await api.post("/payments/checkout", checkoutPayload(`${planId}${suffix}`));
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="relative max-w-4xl" data-testid="radar-page">
      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
        <RadarIcon size={12} /> LEO RADAR · POLSKA
      </div>
      <h1 className="serif text-4xl sm:text-5xl text-stone-100 mt-2">
        Produkcje na radarze
      </h1>
      <p className="text-stone-300 mt-4 leading-relaxed max-w-2xl">
        Filmy i seriale, o których warto wiedzieć wcześniej.
      </p>
      <div className="mt-5 paper p-4 border-stone-800 text-stone-300 text-sm leading-relaxed">
        <p>
          Monitorujemy publicznie dostępne informacje o projektach filmowych i
          serialowych. Część projektów ma potwierdzony etap realizacji, a część
          obserwujemy na podstawie świeżych sygnałów branżowych. Brak
          publicznych informacji o obsadzie lub castingu nie oznacza, że casting
          jest otwarty.
        </p>
        <p className="mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mt-4">
          Ostatnia aktualizacja: {RADAR_LAST_UPDATE} · Kolejna aktualizacja:{" "}
          {RADAR_NEXT_UPDATE}
        </p>
      </div>

      {unlocked ? (
        <>
          {/* Section 1 — POTWIERDZONY ETAP */}
          <section className="mt-10" data-testid="radar-section-confirmed">
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
              Sekcja 01
            </div>
            <h2 className="serif text-3xl text-stone-100 mt-1">
              POTWIERDZONY ETAP
            </h2>
            <p className="text-stone-400 text-sm mt-2 max-w-2xl">
              Projekty, których aktualny etap udało się potwierdzić publicznie.
            </p>
            <div
              className="mt-5 space-y-4"
              data-testid="radar-productions-list"
            >
              {RADAR_CONFIRMED.map((p) => (
                <ConfirmedCard key={p.id} p={p} />
              ))}
            </div>
          </section>

          {/* Section 2 — DO OBSERWACJI */}
          <section className="mt-12" data-testid="radar-section-observation">
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
              Sekcja 02
            </div>
            <h2 className="serif text-3xl text-stone-100 mt-1">
              DO OBSERWACJI
            </h2>
            <p className="text-stone-400 text-sm mt-2 max-w-2xl">
              Projekty, przy których pojawił się świeży, wiarygodny sygnał
              branżowy, ale dokładny etap realizacji nie został jeszcze
              publicznie potwierdzony.
            </p>
            <div className="mt-5 space-y-4">
              {RADAR_OBSERVATION.map((p) => (
                <ObservationCard key={p.id} p={p} />
              ))}
            </div>
          </section>

          <section className="mt-12" data-testid="radar-section-archive">
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
              Sekcja 03
            </div>
            <h2 className="serif text-3xl text-stone-100 mt-1">
              ARCHIWUM
            </h2>
            <p className="text-stone-400 text-sm mt-2">
              Produkcje zakończone.
            </p>
            <div className="mt-5 space-y-4">
              {RADAR_ARCHIVE.map((p) => (
                <ConfirmedCard key={p.id} p={p} />
              ))}
            </div>
          </section>
        </>
      ) : (
        <section
          data-testid="radar-paywall"
          className="mt-10"
        >
          <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
            Sekcja 01
          </div>
          <h2 className="serif text-3xl text-stone-100 mt-1">
            POTWIERDZONY ETAP
          </h2>
          <p className="text-stone-400 text-sm mt-2 max-w-2xl">
            Projekty, których aktualny etap udało się potwierdzić publicznie.
          </p>
          <div
            className="mt-5"
            data-testid="radar-productions-list"
          >
            <ConfirmedCard p={sampleForFree} />
          </div>
          <div className="mt-8" data-testid="radar-plan-picker">
            <h3 className="serif text-2xl sm:text-3xl text-stone-100">
              Odblokuj pełny LEO Radar
            </h3>
            <p className="text-stone-400 mt-3 text-sm leading-relaxed max-w-2xl">
              Śledź produkcje filmowe i serialowe. Pełny Radar jest dostępny w
              planach LEO START, LEO MONTHLY i LEO ANNUAL.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {unlockPlans.map((plan) => (
                <div
                  key={plan.id}
                  data-testid={`radar-plan-${plan.id}`}
                  className="border border-stone-800 rounded-2xl p-5 bg-stone-900/60 flex flex-col"
                >
                  <div className="serif text-xl text-stone-100">{plan.name}</div>
                  <div className="mono text-xs uppercase tracking-widest text-amber-400 mt-2">
                    {plan.price}{" "}
                    <span className="text-stone-500">{plan.priceSuffix || ""}</span>
                  </div>
                  <CheckoutConsentCheckboxes
                    lang={lang}
                    planId={plan.id}
                    consent={consentOf(plan.id)}
                    onChange={(k, v) => setConsent(plan.id, k, v)}
                    testidPrefix="radar-paywall-consent"
                  />
                  <div className="flex-1" />
                  <button
                    data-testid={`radar-buy-${plan.id}`}
                    disabled={busy === plan.id || !canBuy(plan.id)}
                    onClick={() => buy(plan.id)}
                    className="pill-btn mt-4 w-full disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busy === plan.id
                      ? (lang === "pl" ? "Otwieram Stripe…" : "Opening Stripe…")
                      : `${lang === "pl" ? "Wybierz" : "Choose"} ${plan.name.replace(/^LEO\s+/i, "")}`}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
